/**
 * Theme management. We maintain two palettes — `alkisen-dark` (default,
 * sommelier mood) and `alkisen-light` (warm parchment background) — both
 * declared as CSS-custom-property scopes in styles/globals.css.
 *
 * Selection persists in localStorage and respects `prefers-color-scheme`
 * on first visit when the user has expressed no preference yet.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeName = 'alkisen-dark' | 'alkisen-light';

interface ThemeStore {
  theme: ThemeName;
  setTheme: (t: ThemeName) => void;
  toggle: () => void;
}

const VALID_THEMES: ReadonlySet<ThemeName> = new Set<ThemeName>(['alkisen-dark', 'alkisen-light']);

export const useTheme = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: 'alkisen-dark',
      setTheme: (theme) => set({ theme }),
      // Defensive toggle: any value that isn't *explicitly* light is treated
      // as dark and flipped to light. This means leftover legacy persisted
      // values from earlier brand renames (`vinkjeller-dark`, `alkis-dark`)
      // still flip correctly on the first click instead of writing back the
      // current canonical name and visually doing nothing.
      toggle: () =>
        set({ theme: get().theme === 'alkisen-light' ? 'alkisen-dark' : 'alkisen-light' }),
    }),
    {
      name: 'ui-theme',
      // Bump the storage version when the legal `ThemeName` set changes so
      // pre-rebrand persisted values get reset to the default on next load.
      version: 2,
      onRehydrateStorage: () => (state) => {
        if (state && !VALID_THEMES.has(state.theme)) {
          state.theme = 'alkisen-dark';
        }
      },
    },
  ),
);

/** Apply the active theme to <html data-theme> and the <meta theme-color>. */
export function useThemeEffect(): void {
  const theme = useTheme((s) => s.theme);
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', theme === 'alkisen-dark' ? '#0e0a08' : '#efe5d3');
    }
  }, [theme]);
}
